/**
 * Tool Sorcerer tap drill and threading guide, with the drill and tap finder.
 *
 * Two jobs: tapping a metric thread, or drilling a hole. The material (ISO 513 group) picks the
 * drill and the lubricant. Every word comes from the locale file or the section settings through the
 * JSON block in the section; this file holds only numbers and logic.
 *
 * Finder: the section names the drill and tap collections; their sizes are read once from
 * /collections/<handle>?view=finder-data (templates/collection.finder-data.liquid). The answer then
 * shows the exact drill and tap with price, stock pill and Add to cart, or an "Ask us" link when the
 * size is not listed yet. Sizes are read from the variant name, then the product name:
 * drills "6.8mm", "6.8mm / Single", "6mm / Pack of 10" or "1/16 (1.6mm)", taps "M8" (coarse) or
 * "M8 x 1". The drill and tap type comes from the product name, type and tags (HSS, cobalt / HSS-Co /
 * HSS-E, carbide; taper, plug, bottoming, set, spiral point, spiral flute).
 *
 * Own brand first: for a drill the finder offers our own (TS Tools) size, and where a product tagged
 * "premium" (Guhring) has the same size and type it is shown underneath as the step up. Singles are
 * preferred to packs of 10 when a size comes both ways.
 *
 * Without JavaScript the section shows a plain metric tap drill table instead.
 */
(() => {
  if (customElements.get('thread-guide')) return;

  // Metric threads: diameter, coarse pitch, common fine pitches (ISO 261 / 262)
  const METRIC = [
    [3, 0.5, []],
    [4, 0.7, []],
    [5, 0.8, [0.5]],
    [6, 1, [0.75]],
    [8, 1.25, [1, 0.75]],
    [10, 1.5, [1.25, 1]],
    [12, 1.75, [1.5, 1.25]],
    [14, 2, [1.5]],
    [16, 2, [1.5]],
    [18, 2.5, [2, 1.5]],
    [20, 2.5, [2, 1.5]],
    [22, 2.5, [2, 1.5]],
    [24, 3, [2, 1.5]],
    [27, 3, [2]],
    [30, 3.5, [2]],
  ];

  // The best drill for each ISO material group
  const BEST_DRILL = { P: 'hss', M: 'hssco', K: 'hss', N: 'hss', S: 'carbide', H: 'carbide' };
  // Drills the finder may offer for each group, best first (hardened steel: carbide only)
  const DRILL_CHOICES = { P: ['hss', 'hssco'], M: ['hssco', 'hss'], K: ['hss', 'hssco'], N: ['hss', 'hssco'], S: ['carbide', 'hssco'], H: ['carbide'] };
  // Taps the finder may offer for each job, best first; any tap of the right thread comes after these
  const TAP_CHOICES = {
    hand_through: ['taper', 'set', 'spiral_point', 'plug'],
    hand_blind: ['set', 'bottoming', 'plug', 'taper'],
    machine_through: ['spiral_point', 'taper', 'set'],
    machine_blind: ['spiral_flute', 'bottoming', 'set'],
  };
  // Stock pill states, best first
  const STATE_RANK = { 'in-stock': 3, 'order-in': 2, 'long-lead': 1 };

  const trim = (value, places = 2) => Number(value.toFixed(places)).toString();
  const round = (value, step) => Math.round(value / step) * step;
  const same = (a, b) => Math.abs(a - b) < 0.001;
  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Tap drill: diameter minus pitch, to the drill the ISO charts use. A half-way value rounds to the
  // even tenth, which is how the published tables read (M12: 10.25 -> 10.2, M10 x 1.25: 8.75 -> 8.8).
  const tapDrill = (d, p) => {
    const tenths = Number(trim((d - p) * 10, 4));
    const floor = Math.floor(tenths);
    let result = Math.round(tenths);
    if (Math.abs(tenths - floor - 0.5) < 1e-6) result = floor % 2 === 0 ? floor : floor + 1;
    return Number(trim(result / 10));
  };

  const drillKind = (text) => {
    if (/carbide/.test(text)) return 'carbide';
    if (/cobalt|hss-?co|hss-?e\b|\bm35\b|\bm42\b/.test(text)) return 'hssco';
    return 'hss';
  };

  const tapKind = (text) => {
    if (/spiral[\s-]*point|gun/.test(text)) return 'spiral_point';
    if (/spiral[\s-]*flute/.test(text)) return 'spiral_flute';
    if (/\bsets?\b/.test(text)) return 'set';
    if (/bottom/.test(text)) return 'bottoming';
    if (/plug|second|intermediate/.test(text)) return 'plug';
    if (/taper|first/.test(text)) return 'taper';
    return 'other';
  };

  // A size's name: the variant ("6.8mm / Single") unless it is the only one ("Default Title")
  const sizeName = (item) => (item.variant && item.variant !== 'Default Title' ? item.variant : '');

  // Drill size from the variant: the first "<number>mm" ("6.8mm / Single", "1/16 (1.6mm)"), or a bare
  // number ("6.8"). The product name is only used for a product with a single size, so a range in the
  // title ("1–12.2mm") or a pack ("Pack of 10") is never read as the size.
  const parseDrill = (item) => {
    const variant = sizeName(item);
    let match = null;
    if (variant) {
      match = variant.match(/(\d+(?:[.,]\d+)?)\s*mm\b/i) || variant.trim().match(/^(\d+(?:[.,]\d+)?)$/);
    } else {
      match = item.product.match(/(\d+(?:[.,]\d+)?)\s*mm\b/i);
    }
    if (!match) return null;
    const text = item.text.toLowerCase();
    const pack = variant.match(/pack\s*of\s*(\d+)/i);
    return {
      ...item,
      size: Number(match[1].replace(',', '.')),
      kind: drillKind(text),
      pack: pack ? Number(pack[1]) : 1,
      premium: /\bpremium\b|guhring/.test(text),
    };
  };

  const parseTap = (item) => {
    const pattern = /\bM\s?(\d+(?:\.\d+)?)(?:\s*[x×]\s*(\d+(?:\.\d+)?))?/i;
    const match = sizeName(item).match(pattern) || item.product.match(pattern);
    if (!match) return null;
    const text = item.text.toLowerCase();
    if (/\bdies?\b/.test(text) && !/\btaps?\b/.test(text)) return null;
    const d = Number(match[1]);
    const coarse = (METRIC.find(([size]) => size === d) || [])[1];
    const p = match[2] ? Number(match[2]) : coarse;
    if (!p) return null;
    return { ...item, d, p, kind: tapKind(`${sizeName(item)} ${text}`.toLowerCase()) };
  };

  // Best first: in stock beats a lead time, then a single beats a pack
  const order = (a, b) => STATE_RANK[b.state] - STATE_RANK[a.state] || (a.pack || 1) - (b.pack || 1);

  // The best size from kinds in order of preference
  const pick = (hits, kinds, anyKind) => {
    for (const kind of kinds) {
      const best = hits.filter((item) => item.kind === kind).sort(order)[0];
      if (best) return best;
    }
    return anyKind ? hits.slice().sort(order)[0] || null : null;
  };

  class ThreadGuide extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-guide-form]');
      this.result = this.querySelector('[data-guide-result]');
      if (!this.form || !this.result) return;
      try {
        this.t = JSON.parse(this.querySelector('[data-guide-strings]').textContent);
      } catch (error) {
        this.t = {};
      }
      // The t filter HTML-escapes its output (maker&#39;s); decode once so escapeHtml does not escape it twice
      const decoder = document.createElement('textarea');
      Object.keys(this.t).forEach((key) => {
        decoder.innerHTML = this.t[key];
        this.t[key] = decoder.value;
      });
      this.size = this.form.elements.size;
      this.pitch = this.form.elements.pitch;
      this.diameter = this.form.elements.diameter;
      this.stock = null;
      this.byId = {};

      this.size.innerHTML = METRIC.map(([d]) => `<option value="${d}">M${d}</option>`).join('');
      this.size.value = '8';
      this.fillPitches();

      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.form.addEventListener('change', (event) => {
        if (event.target === this.size) this.fillPitches();
        this.render();
      });
      this.form.addEventListener('input', (event) => {
        if (event.target === this.diameter) this.render();
      });
      this.result.addEventListener('click', (event) => {
        const button = event.target.closest('[data-add]');
        if (button) this.addToCart(button);
      });

      // Phones: the guide opens from a button so the home page stays short
      const opener = this.querySelector('[data-guide-open]');
      if (opener) {
        opener.addEventListener('click', () => {
          this.classList.add('is-open');
          opener.setAttribute('aria-expanded', 'true');
          this.form.querySelector('input, select')?.focus();
        });
      }

      this.render();
      this.loadStock();
    }

    value(name) {
      return this.form.elements[name]?.value ?? '';
    }

    text(key, values = {}) {
      let out = this.t[key] || '';
      Object.entries(values).forEach(([name, value]) => {
        out = out.split(`[${name}]`).join(value);
      });
      return out;
    }

    fillPitches() {
      const [, coarse, fines] = METRIC.find(([d]) => String(d) === this.size.value) || METRIC[0];
      this.pitch.innerHTML = [
        `<option value="${coarse}">${escapeHtml(this.text('pitch_coarse', { pitch: coarse }))}</option>`,
        ...fines.map((p) => `<option value="${p}">${escapeHtml(this.text('pitch_fine', { pitch: p }))}</option>`),
      ].join('');
    }

    // Every size in the drill and tap collections, read once; the answer re-renders when they arrive
    async loadStock() {
      const read = async (url, parse) => {
        if (!url) return [];
        const items = [];
        for (let page = 1, pages = 1; page <= pages && page <= 20; page += 1) {
          const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}page=${page}`);
          if (!response.ok) break;
          const json = await response.json();
          pages = json.pages || 1;
          items.push(...json.items);
        }
        return items.map(parse).filter(Boolean);
      };
      if (!this.dataset.drillData && !this.dataset.tapData) return;
      try {
        const [drills, taps] = await Promise.all([
          read(this.dataset.drillData, parseDrill),
          read(this.dataset.tapData, parseTap),
        ]);
        this.stock = { drills: drills.filter((item) => item.state), taps: taps.filter((item) => item.state) };
        [...this.stock.drills, ...this.stock.taps].forEach((item) => {
          this.byId[item.id] = item;
        });
        this.render();
      } catch (error) {
        this.stock = null;
      }
    }

    // Shop buttons go to the page set in the theme editor (drill or tap); with no link or text set, no button
    link(kind) {
      const url = this.dataset[`${kind}Url`];
      const label = this.dataset[`${kind}Label`];
      if (!url || !label) return '';
      return `<a class="ts-guide__link" href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
    }

    links(kinds) {
      const buttons = kinds.map((kind) => this.link(kind)).filter(Boolean);
      return buttons.length ? `<p class="ts-guide__links">${buttons.join('')}</p>` : '';
    }

    // One size from the finder: picture, name, price, stock pill and Add to cart. A premium size gets a
    // small "Premium upgrade" label above its name.
    item(entry, premium = false) {
      const name = sizeName(entry) ? `${entry.product} – ${sizeName(entry)}` : entry.product;
      const pillKey = entry.state.replace('-', '_');
      const flag = premium
        ? `<span class="ts-finder__flag" style="display:block;font-size:1.2rem;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;opacity:0.75">${escapeHtml(this.text('premium') || 'Premium upgrade')}</span>`
        : '';
      return `<li class="ts-finder__item">` +
        (entry.image ? `<img class="ts-finder__img" src="${escapeHtml(entry.image)}" alt="" width="56" height="56" loading="lazy">` : '<span class="ts-finder__img"></span>') +
        `<span class="ts-finder__text">${flag}<a href="${escapeHtml(entry.url)}">${escapeHtml(name)}</a>` +
        `<span class="ts-finder__meta"><b>${escapeHtml(entry.price)}</b>` +
        `<span class="stock-badge stock-badge--${entry.state}">${escapeHtml(this.text(pillKey))}</span></span></span>` +
        `<button type="button" class="ts-finder__add" data-add="${entry.id}">${escapeHtml(this.text('add'))}</button></li>`;
    }

    missing(what) {
      const url = new URL(this.dataset.contactUrl || '/pages/contact', window.location.origin);
      url.searchParams.set('enquiry', this.text('enquiry', { item: what }));
      url.hash = 'message';
      return `<li class="ts-finder__missing">${escapeHtml(this.text('missing', { item: what }))} ` +
        `<a href="${escapeHtml(url.pathname + url.search + url.hash)}">${escapeHtml(this.text('ask'))}</a></li>`;
    }

    // The finder's part of an answer; until the sizes have loaded, the plain shop buttons. "found" is
    // the drill (and tap), each [entry, description, premium alternative].
    finder(found, fallbackKinds) {
      if (!this.stock) return this.links(fallbackKinds);
      const items = [];
      found.forEach(([entry, what, upgrade]) => {
        items.push(entry ? this.item(entry) : this.missing(what));
        if (upgrade) items.push(this.item(upgrade, true));
      });
      const both = found.length > 1 && found.every(([entry]) => entry);
      return `<div class="ts-finder"><ul class="ts-finder__list">${items.join('')}</ul>` +
        (both ? `<button type="button" class="ts-finder__both" data-add="${found.map(([entry]) => entry.id).join(',')}">${escapeHtml(this.text('add_both'))}</button>` : '') +
        `</div>`;
    }

    answer(big, sub, rows, extra) {
      const items = rows
        .filter((row) => row && row[1])
        .map(([label, value]) => `<div class="ts-guide__row"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`);
      return `<p class="ts-guide__headline"><span class="ts-guide__big">${escapeHtml(big)}</span>` +
        `<span class="ts-guide__sub">${escapeHtml(sub)}</span></p>` +
        `<dl class="ts-guide__rows">${items.join('')}</dl>` +
        extra;
    }

    render() {
      const job = this.value('job') || 'tap';
      this.form.querySelectorAll('[data-show]').forEach((el) => {
        el.hidden = el.dataset.show !== job;
      });
      const material = this.value('material') || 'P';
      const html = job === 'drill' ? this.answerDrill(material) : this.answerTap(material);
      this.result.innerHTML = `${html}<p class="ts-guide__note">${escapeHtml(this.text('note'))}</p>`;
    }

    // The drill for a size: our own brand first, and the premium (Guhring) drill of the same type as the
    // step up. Only premium drills of that size: the premium one is the answer, with no step up.
    findDrill(size, material) {
      if (!this.stock) return [null, null];
      const kinds = DRILL_CHOICES[material] || ['hss'];
      const hits = this.stock.drills.filter((item) => same(item.size, size));
      const own = pick(hits.filter((item) => !item.premium), kinds, false);
      const premiumHits = hits.filter((item) => item.premium);
      if (!own) return [pick(premiumHits, kinds, false), null];
      return [own, pick(premiumHits, [own.kind], false)];
    }

    findTap(d, p, job) {
      if (!this.stock) return null;
      const hits = this.stock.taps.filter((item) => same(item.d, d) && same(item.p, p));
      return pick(hits, TAP_CHOICES[job] || [], true);
    }

    answerTap(material) {
      const row = METRIC.find(([d]) => String(d) === this.size.value) || METRIC[0];
      const d = row[0];
      const p = Number(this.pitch.value) || row[1];
      const coarse = p === row[1];
      const thread = coarse ? `M${d}` : `M${d} × ${p}`;
      const drill = tapDrill(d, p);
      const blind = this.value('hole') === 'blind';
      const machine = this.value('by') === 'machine';
      const tool = this.text(`drill_${BEST_DRILL[material]}`);
      const job = `${machine ? 'machine' : 'hand'}_${blind ? 'blind' : 'through'}`;
      const tapName = this.text(`tap_${job}`);
      const [drillEntry, drillUpgrade] = this.findDrill(drill, material);

      return this.answer(
        `${trim(drill)} mm`,
        this.text('a_tap_drill', { thread }),
        [
          [this.text('a_drill'), blind
            ? this.text('a_drill_blind', { tool, extra: `${trim(3 * p)} mm` })
            : this.text('a_drill_through', { tool })],
          [this.text('a_tap'), tapName],
          [this.text('a_lube'), this.text(`lube_${material}`)],
        ],
        this.finder(
          [
            [drillEntry, `${trim(drill)} mm ${tool} drill`, drillUpgrade],
            [this.findTap(d, p, job), `${thread} ${tapName.toLowerCase()}`, null],
          ],
          ['drill', 'tap']
        )
      );
    }

    answerDrill(material) {
      const d = parseFloat(String(this.diameter.value || '').replace(',', '.'));
      if (!d || d <= 0) return `<p class="ts-guide__empty">${escapeHtml(this.text('enter_size'))}</p>`;
      const size = `${trim(d)} mm`;
      const tool = this.text(`drill_${BEST_DRILL[material]}`);
      const [drillEntry, drillUpgrade] = this.findDrill(d, material);
      return this.answer(
        tool,
        `${this.text('a_best_drill', { material: this.text(`mat_${material}`).toLowerCase() })}, ${size}`,
        [
          [this.text('a_also'), this.text(`also_${material}`)],
          [this.text('a_lube'), this.text(`lube_${material}`)],
          d > 13 && [this.text('a_pilot'), this.text('a_pilot_value', { size: `${trim(round(d / 3, 0.5))} mm` })],
        ],
        this.finder([[drillEntry, `${size} ${tool} drill`, drillUpgrade]], ['drill'])
      );
    }

    // Adds one size, or the drill and tap together, with each size's stock pill as a line item
    // property (window.tsSite.property, "Availability"), then opens the cart drawer
    async addToCart(button) {
      const property = window.tsSite?.property || 'Availability';
      const items = button.dataset.add.split(',').map((id) => {
        const entry = this.byId[id];
        const item = { id: Number(id), quantity: 1 };
        if (entry?.state) item.properties = { [property]: this.text(entry.state.replace('-', '_')) };
        return item;
      });
      const cart = document.querySelector('cart-drawer');
      const drawer = cart && typeof cart.renderContents === 'function';
      const body = { items };
      if (drawer) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (state.status) {
          button.textContent = state.description || state.message || label;
          return;
        }
        if (!drawer) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }
        cart.classList.remove('is-empty');
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        cart.renderContents(state);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'thread-guide', cartData: state });
        }
        button.textContent = this.text('added');
        setTimeout(() => {
          button.textContent = label;
        }, 2500);
      } catch (error) {
        button.textContent = window.cartStrings?.error || label;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('thread-guide', ThreadGuide);
})();
